import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AddCommentIcon from "@mui/icons-material/AddComment";
import {
  Box,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { GraphQLClient } from "graphql-request";
import { formatEnum } from "../../utils/formatEnum";
import {
  contactStatusTone,
  engagementTone,
  fitScoreTone,
  relationshipScoreTone,
  followUpTone,
  booleanRiskTone,
} from "../../status/StatusChipTones";

import Header from "../../components/Header";
import ArchiveContactModal from "../../components/ArchiveContactModal";
import CreateInteractionModal from "../../components/CreateInteractionModal";
import TableActions from "../../components/TableActions";
import ActionButton from "../../components/ActionButton";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import ContactMap from "../../components/ContactMap";
import StatusChip from "../../components/StatusChip";


import { graphqlEndpoint } from "../../config";
import { formatRelativeDate } from "../../utils/formatRelativeDate";
import formOptions from "../../config/formOptions.json";

import { GET_ALL_CONTACTS } from "../../graph/contacts/queries";
import { ARCHIVE_CONTACT } from "../../graph/contacts/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const engagementLevelLabelMap = {
  COLD: "Cold",
  OBSERVING: "Observing",
  LIGHTLY_ENGAGED: "Lightly Engaged",
  ENGAGED: "Engaged",
  ACTIVE: "Active",
};

const ManageContacts = () => {
  const navigate = useNavigate();

  const [contacts, setContacts] = useState([]);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [interactionModalOpen, setInteractionModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const [viewMode, setViewMode] = useState("table");
  const [industryFilter, setIndustryFilter] = useState("");

  const filteredContacts = industryFilter
    ? contacts.filter((contact) => contact.industry === industryFilter)
    : contacts;

  const fetchContacts = useCallback(async () => {
    try {
      const data = await client.request(GET_ALL_CONTACTS);

      const formattedContacts = data.readAllContacts.map((contact) => ({
        id: contact.id,
        firstName: contact.firstName || "",
        lastName: contact.lastName || "",
        name: [contact.firstName, contact.lastName].filter(Boolean).join(" "),
        status: contact.status || "TARGETED",
        engagementLevel: contact.engagementLevel || "",
        fitScore:
          contact.fitScore === null || contact.fitScore === undefined
            ? null
            : Number(contact.fitScore),
        relationshipScore: contact.relationshipScore || 0,
        lastMeaningfulTouchAt: contact.lastMeaningfulTouchAt || null,
        nextFollowUpAt: contact.nextFollowUpAt || null,
        atRisk: Boolean(contact.atRisk),
        companyName: contact.companyName || "",
        companyID: contact.companyID || null,
        preferredCommsMethod: contact.preferredCommsMethod || "",
        source: contact.source || "",
      }));

      setContacts(formattedContacts);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleOpenArchiveModal = (contact) => {
    setSelectedContact(contact);
    setArchiveModalOpen(true);
  };

  const handleCloseArchiveModal = () => {
    setArchiveModalOpen(false);
    setSelectedContact(null);
  };

  const handleOpenInteractionModal = (contact) => {
    setSelectedContact(contact);
    setInteractionModalOpen(true);
  };

  const handleCloseInteractionModal = () => {
    setInteractionModalOpen(false);
    setSelectedContact(null);
  };

  const handleConfirmArchive = async () => {
    if (!selectedContact?.id) return;

    try {
      await client.request(ARCHIVE_CONTACT, {
        id: selectedContact.id,
      });

      await fetchContacts();
      handleCloseArchiveModal();
    } catch (error) {
      console.error("Failed to archive contact:", error);
    }
  };

  const handleEdit = (id) => {
    navigate(`/contacts/edit/${id}`);
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.3,
      cellClassName: "name-column--cell",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.95,
      renderCell: ({ value }) => (
        <StatusChip
          label={formatEnum(value)}
          tone={contactStatusTone(value)}
        />
      ),
    },
    {
      field: "engagementLevel",
      headerName: "Engagement",
      flex: 1.05,
      renderCell: ({ value }) => {
        if (!value) return "—";

        return (
          <StatusChip
            label={engagementLevelLabelMap[value]}
            tone={engagementTone(value)}
          />
        );
      },
    },
    {
      field: "fitScore",
      headerName: "Fit",
      flex: 0.7,
      type: "number",
      renderCell: ({ value }) => {
        if (value === null || value === undefined) return "—";

        return <StatusChip
          label={value}
          tone={fitScoreTone(value)}
        />;
      },
    },
    {
      field: "relationshipScore",
      headerName: "Score",
      flex: 0.75,
      type: "number",
      renderCell: ({ value }) => (
        <StatusChip
          label={Math.round(value)}
          tone={relationshipScoreTone(value)}
        />
      ),
    },
    {
      field: "lastMeaningfulTouchAt",
      headerName: "Last Touch",
      flex: 1,
      renderCell: ({ value }) => formatRelativeDate(value),
    },
    {
      field: "nextFollowUpAt",
      headerName: "Next Follow-Up",
      flex: 1,
      renderCell: ({ value }) => {
        if (!value) return "—";

        const overdue = new Date(value) < new Date();

        return (
          <StatusChip
            label={formatRelativeDate(value)}
            tone={followUpTone(value)}
          />
        );
      },
    },
    {
      field: "atRisk",
      headerName: "At Risk",
      flex: 0.75,
      renderCell: ({ value }) => (
        <StatusChip
          label={value ? "At Risk" : "No"}
          tone={booleanRiskTone(value)}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.5,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box display="flex" gap={1} alignItems="center">
          <Tooltip title="Add Interaction">
            <IconButton
              size="small"
              sx={{
                backgroundColor: "secondary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "secondary.dark",
                },
              }}
              onClick={() => handleOpenInteractionModal(row)}
            >
              <AddCommentIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <TableActions
            onEdit={() => handleEdit(row.id)}
            onDelete={() => handleOpenArchiveModal(row)}
            hideCreate
          />
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="MANAGE CONTACTS" subtitle="View and manage your contacts" />

        <Box display="flex" gap={1}>
          <ActionButton
            tone="teal"
            variant="outlined"
            onClick={() => navigate("/createContact/event")}
          >
            Event Contact
          </ActionButton>

          <ActionButton
            tone="gold"
            onClick={() => navigate("/createContact")}
          >
            Create Contact
          </ActionButton>
        </Box>
      </Box>

      <Box display="flex" gap={2} alignItems="center" mb={2}>

          <ActionButton
            tone="gold"
            selected={viewMode === "table"}
            variant={viewMode === "table" ? "contained" : "outlined"}
            onClick={() => setViewMode("table")}
          >
            Table
          </ActionButton>

          <ActionButton
            tone="teal"
            variant="outlined"
            selected={viewMode === "map"}
            variant={viewMode === "map" ? "contained" : "outlined"}
            onClick={() => setViewMode("map")}
          >
            Map
          </ActionButton>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Industry</InputLabel>
          <Select
            value={industryFilter}
            label="Industry"
            onChange={(e) => setIndustryFilter(e.target.value)}
          >
            <MenuItem value="">All Industries</MenuItem>

            {formOptions.industryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {viewMode === "table" ? (
        <ThemedDataGrid
          rows={filteredContacts}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: "relationshipScore", sort: "desc" }],
            },
          }}
        />
      ) : (
        <ContactMap industryFilter={industryFilter} />
      )}

      <ArchiveContactModal
        open={archiveModalOpen}
        onClose={handleCloseArchiveModal}
        onConfirm={handleConfirmArchive}
        contact={selectedContact}
      />

      <CreateInteractionModal
        open={interactionModalOpen}
        onClose={handleCloseInteractionModal}
        onSuccess={fetchContacts}
        contact={selectedContact}
      />
    </Box>
  );
};

export default ManageContacts;